/**
 * Photobook-product lifecycle (standalone book, not the fotoclass class-album).
 * Formal transition table — the same discipline the fotoclass photobooks module runs;
 * server enforces it, the frontend derives available actions from it. Never switch
 * status by ad-hoc field writes.
 *
 * draft → clientReview («На утверждении клиентом») → editsReceived («Получены правки»)
 *       → approved («Дизайн завершён»). Print files export without client approval and never move the status —
 * except `print`, which freezes the book as approved under the print lock and orders the files.
 */
export const BOOK_STATUSES = ['draft', 'clientReview', 'editsReceived', 'approved'] as const;
export type BookStatus = (typeof BOOK_STATUSES)[number];

/**
 * send — photographer shares with the client (draft|editsReceived → clientReview);
 * submitEdits — client sends edits/comments back (clientReview → editsReceived);
 * approve — client approves the design, or the photographer approves it for the client (clientReview → approved);
 * interrupt — photographer-only «Прервать утверждение» (clientReview → draft);
 * unapprove — photographer-only «Снять утверждение» back into work (approved → draft);
 * print — photographer-only «Отправить в печать» (draft|editsReceived|approved → approved): the book is
 *   print-locked and its print archive is kept for the lab. Not from clientReview: the client holds the book.
 */
export const BOOK_STATUS_ACTIONS = ['send', 'submitEdits', 'approve', 'interrupt', 'unapprove', 'print'] as const;
export type BookStatusAction = (typeof BOOK_STATUS_ACTIONS)[number];

/**
 * Why a print render ended in `failed` — the book's OWN taxonomy, one code per real refusal of
 * this pipeline: the task never left core, the renderer answered with an error, nobody answered at
 * all. It lives beside the status table because both sides read it off the same row: core stamps
 * `book_print_exports.reason_code`, the editor turns the code into the sentence the photographer
 * reads. The fotoclass `PHOTOBOOK_ARCHIVE_FAILURE_REASONS` is a different product's vocabulary
 * (class-album archive) and is deliberately not widened to cover this one.
 */
export const BOOK_PRINT_FAILURE_REASONS = [
  'publish_failed',
  'worker_error',
  'watchdog_timeout',
  'render_rule_stale',
  'photo_unavailable',
] as const;
export type BookPrintFailureReason = (typeof BOOK_PRINT_FAILURE_REASONS)[number];

/** Why a bulk delete left a book in place; `exportPending` — its print files are still being built. */
export type BookBulkDeleteRejectReason = 'notFound' | 'forbidden' | 'exportPending';

/**
 * The only actions the public status route accepts from the owner. `approve` is the client's
 * verb and the photographer's «Утвердить за клиента» alike — the same readiness gate judges both;
 * `print` is judged by the print-files gate, with nothing to override.
 * Everything else moves a book solely through its own internal path: send via the share route,
 * submitEdits via the client link.
 */
export const PUBLIC_BOOK_STATUS_ACTIONS = [
  'approve',
  'interrupt',
  'unapprove',
  'print',
] as const satisfies readonly BookStatusAction[];
export type PublicBookStatusAction = (typeof PUBLIC_BOOK_STATUS_ACTIONS)[number];

const TRANSITIONS: Readonly<Record<BookStatusAction, Partial<Record<BookStatus, BookStatus>>>> = {
  send: { draft: 'clientReview', editsReceived: 'clientReview' },
  submitEdits: { clientReview: 'editsReceived' },
  approve: { clientReview: 'approved' },
  interrupt: { clientReview: 'draft' },
  unapprove: { approved: 'draft' },
  print: { draft: 'approved', editsReceived: 'approved', approved: 'approved' },
};

/** Resulting status for an action, or null when the action is not legal from `from`. */
export function bookStatusTransition(from: BookStatus, action: BookStatusAction): BookStatus | null {
  return TRANSITIONS[action][from] ?? null;
}

/**
 * `null` locks (status not known yet); `clientReview` hands the book to the client; `approved` freezes the layout.
 * Fail-closed: a stored status outside `BOOK_STATUSES` (a row an older pod wrote mid-rollout) locks too.
 */
export function isPhotographerLocked(status: BookStatus | null): boolean {
  return status !== 'draft' && status !== 'editsReceived';
}

/**
 * Замок КЛИЕНТСКОЙ ссылки: «На утверждении» — это ровно то время, когда клиент книгу и правит
 * (`isClientEditWindow`), поэтому запирает его только утверждение. `null` (статус ещё не
 * известен) и статус вне `BOOK_STATUSES` запирают, как и у фотографа.
 */
export function isClientLocked(status: BookStatus | null): boolean {
  return status !== 'draft' && status !== 'clientReview' && status !== 'editsReceived';
}

/** The client editor mutates the model only during review and only with the edit toggle on. */
export function isClientEditWindow(status: BookStatus): boolean {
  return status === 'clientReview';
}

/** Why a client link may not edit the book right now: a comment-only link, or a book outside its client edit window. */
export type ClientEditRefusal = 'comment-only' | 'not-in-window';

/**
 * The one rule behind every client edit through a link: the model save (core), the photo marks (gateway) and the
 * client editor role. `null` = the link may edit. Each side maps the reason to its own answer: comment-only is a
 * missing right (403), a book outside the window is a status conflict (409).
 */
export function clientEditRefusal(mode: 'comment' | 'edit', status: BookStatus): ClientEditRefusal | null {
  if (mode !== 'edit') return 'comment-only';
  return isClientEditWindow(status) ? null : 'not-in-window';
}

/**
 * Имя socket-комнаты книги. Обе стороны читают его отсюда: гейтвей собирает комнату для emit и
 * для проверки владельца, фронт — для входа в неё. Разъехавшиеся половинки не падают, а молча
 * отказывают во входе по имени, поэтому половинка ровно одна.
 */
export const BOOK_SOCKET_ROOM_PREFIX = 'book:operations:';

/** Days a trashed book stays restorable: the purge cron and the trash countdown both read this one number. */
export const BOOK_TRASH_PURGE_DAYS = 30;

/** Days a print archive stays reachable after it was attached; not the export row TTL (`PRINT_ARCHIVE_TTL_MS`). */
export const PRINT_ARCHIVE_RETENTION_DAYS = 30;

/** The day a print link stops working: attach date + retention. One owner for the public page, shelf, editor and sweep. */
export function printArchiveValidUntil(attachedAt: Date | string): Date {
  return new Date(new Date(attachedAt).getTime() + PRINT_ARCHIVE_RETENTION_DAYS * 86_400_000);
}
